<script setup>
import { useCampaignAvailability } from 'dashboard/composables/useCampaignAvailability';
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import CampaignsAPI from 'dashboard/api/campaigns';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const props = defineProps({
  campaignId: { type: [String, Number], required: true },
});

const emit = defineEmits(['retarget']);

const { t } = useI18n();

const contacts = ref([]);
const meta = ref({});
const isLoading = ref(true);
const currentPage = ref(1);
const searchQuery = ref('');
const activeFilter = ref('all');
const selectedIds = ref(new Set());

const STATUS_COLORS = {
  pending: 'bg-n-alpha-2 text-n-slate-11',
  queued: 'bg-n-alpha-2 text-n-slate-11',
  sent: 'bg-[#322F23]/10 text-[#322F23]',
  delivered: 'bg-n-blue-2 text-n-blue-11',
  read: 'bg-n-teal-2 text-n-teal-11',
  failed: 'bg-n-ruby-2 text-n-ruby-11',
  skipped: 'bg-n-alpha-2 text-n-slate-10',
};

const smartFilters = [
  { key: 'all', label: 'All' },
  { key: 'read_not_replied', label: 'Read, not replied' },
  { key: 'delivered_not_read', label: 'Delivered, not read' },
  { key: 'failed_only', label: 'Failed' },
  { key: 'replied', label: 'Replied' },
  { key: 'clicked', label: 'Clicked' },
];

const fetchContacts = async () => {
  isLoading.value = true;
  try {
    const params = { page: currentPage.value };
    if (activeFilter.value !== 'all') params.smart_filter = activeFilter.value;
    if (searchQuery.value) params.search = searchQuery.value;

    const { data } = await CampaignsAPI.getCampaignContacts(
      props.campaignId,
      params
    );
    contacts.value = data.payload;
    meta.value = data.meta;
  } catch {
    contacts.value = [];
  } finally {
    isLoading.value = false;
  }
};

const formatTime = timestamp => {
  if (!timestamp) return '-';
  return new Date(timestamp * 1000).toLocaleString();
};

const setFilter = key => {
  activeFilter.value = key;
  currentPage.value = 1;
  selectedIds.value = new Set();
};

const toggleSelect = id => {
  const newSet = new Set(selectedIds.value);
  if (newSet.has(id)) newSet.delete(id);
  else newSet.add(id);
  selectedIds.value = newSet;
};

const toggleSelectAll = () => {
  if (selectedIds.value.size === contacts.value.length) {
    selectedIds.value = new Set();
  } else {
    selectedIds.value = new Set(contacts.value.map(c => c.id));
  }
};

const isAllSelected = computed(
  () =>
    contacts.value.length > 0 &&
    selectedIds.value.size === contacts.value.length
);

const hasSelection = computed(() => selectedIds.value.size > 0);

const showBulkMenu = ref(false);

const handleBulkAction = actionType => {
  showBulkMenu.value = false;
  emit('retarget', {
    actionType,
    contactIds: Array.from(selectedIds.value),
    filter: activeFilter.value,
  });
};

// Reset state when campaign changes
watch(
  () => props.campaignId,
  () => {
    activeFilter.value = 'all';
    currentPage.value = 1;
    searchQuery.value = '';
    selectedIds.value = new Set();
  }
);

watch(
  [currentPage, activeFilter, searchQuery, () => props.campaignId],
  fetchContacts,
  {
    immediate: true,
  }
);
const { campaignsEnabled } = useCampaignAvailability();
</script>

<template>
  <div class="flex flex-col gap-3">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <h3 class="text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.ANALYTICS.SMART_TABLE.TITLE') }}
      </h3>
      <div class="flex items-center gap-2">
        <div v-if="hasSelection" class="relative">
          <Button
            :label="`${t('CAMPAIGN.WHATSAPP.ANALYTICS.SMART_TABLE.RETARGET')} (${selectedIds.size})`"
            size="sm"
            color="blue"
            icon="i-lucide-target"
            @click="showBulkMenu = !showBulkMenu"
          />
          <div
            v-if="showBulkMenu"
            class="absolute right-0 top-full mt-1 z-20 w-56 rounded-lg border border-n-weak bg-n-solid-2 shadow-lg py-1"
          >
            <button
              type="button"
              class="flex items-center gap-2 w-full px-3 py-2 text-sm text-n-slate-12 hover:bg-n-alpha-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              :disabled="!campaignsEnabled"
              @click="handleBulkAction('new_campaign')"
            >
              <span class="i-lucide-send size-4 text-n-slate-10" />
              {{ t('CAMPAIGN.WHATSAPP.RETARGET.NEW_CAMPAIGN') }}
            </button>
            <button
              type="button"
              class="flex items-center gap-2 w-full px-3 py-2 text-sm text-n-slate-12 hover:bg-n-alpha-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              @click="handleBulkAction('add_tag')"
            >
              <span class="i-lucide-tag size-4 text-n-slate-10" />
              {{ t('CAMPAIGN.WHATSAPP.RETARGET.ADD_TAG') }}
            </button>
            <button
              type="button"
              class="flex items-center gap-2 w-full px-3 py-2 text-sm text-n-slate-12 hover:bg-n-alpha-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              @click="handleBulkAction('webhook')"
            >
              <span class="i-lucide-webhook size-4 text-n-slate-10" />
              {{ t('CAMPAIGN.WHATSAPP.RETARGET.SEND_WEBHOOK') }}
            </button>
          </div>
        </div>
        <Input
          v-model="searchQuery"
          :placeholder="t('CAMPAIGN.WHATSAPP.ANALYTICS.SMART_TABLE.SEARCH')"
          class="w-48"
          size="sm"
        />
      </div>
    </div>

    <!-- Smart filters -->
    <div class="flex items-center gap-1.5">
      <button
        v-for="filter in smartFilters"
        :key="filter.key"
        type="button"
        class="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors cursor-pointer"
        :class="
          activeFilter === filter.key
            ? 'bg-[#322F23] text-white'
            : 'bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3'
        "
        @click="setFilter(filter.key)"
      >
        {{ filter.label }}
      </button>
    </div>

    <!-- Table -->
    <div class="rounded-lg border border-n-weak overflow-hidden">
      <table class="w-full text-sm">
        <thead>
          <tr class="bg-n-alpha-2">
            <th class="w-10 px-3 py-2.5">
              <input
                type="checkbox"
                :checked="isAllSelected"
                class="cursor-pointer"
                @change="toggleSelectAll"
              />
            </th>
            <th
              class="px-3 py-2.5 text-left text-xs font-medium text-n-slate-11"
            >
              {{ t('CAMPAIGN.WHATSAPP.ANALYTICS.SMART_TABLE.NAME') }}
            </th>
            <th
              class="px-3 py-2.5 text-left text-xs font-medium text-n-slate-11"
            >
              {{ t('CAMPAIGN.WHATSAPP.ANALYTICS.SMART_TABLE.PHONE') }}
            </th>
            <th
              class="px-3 py-2.5 text-left text-xs font-medium text-n-slate-11"
            >
              {{ t('CAMPAIGN.WHATSAPP.ANALYTICS.SMART_TABLE.STATUS') }}
            </th>
            <th
              class="px-3 py-2.5 text-left text-xs font-medium text-n-slate-11"
            >
              {{ t('CAMPAIGN.WHATSAPP.ANALYTICS.SMART_TABLE.READ_AT') }}
            </th>
            <th
              class="px-3 py-2.5 text-left text-xs font-medium text-n-slate-11"
            >
              {{ t('CAMPAIGN.WHATSAPP.ANALYTICS.SMART_TABLE.REPLIED_AT') }}
            </th>
            <th
              class="px-3 py-2.5 text-left text-xs font-medium text-n-slate-11"
            >
              {{ t('CAMPAIGN.WHATSAPP.ANALYTICS.SMART_TABLE.ERROR') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="isLoading" class="border-t border-n-weak">
            <td colspan="7" class="px-3 py-8 text-center text-n-slate-10">
              {{ t('CAMPAIGN.WHATSAPP.ANALYTICS.SMART_TABLE.LOADING') }}
            </td>
          </tr>
          <tr v-else-if="contacts.length === 0" class="border-t border-n-weak">
            <td colspan="7" class="px-3 py-8 text-center text-n-slate-10">
              {{ t('CAMPAIGN.WHATSAPP.ANALYTICS.SMART_TABLE.NO_RESULTS') }}
            </td>
          </tr>
          <template v-else>
            <tr
              v-for="cc in contacts"
              :key="cc.id"
              class="border-t border-n-weak hover:bg-n-alpha-1 transition-colors"
            >
              <td class="w-10 px-3 py-2.5">
                <input
                  type="checkbox"
                  :checked="selectedIds.has(cc.id)"
                  class="cursor-pointer"
                  @change="toggleSelect(cc.id)"
                />
              </td>
              <td class="px-3 py-2.5 text-n-slate-12">
                {{ cc.contact?.name || '-' }}
              </td>
              <td class="px-3 py-2.5 text-n-slate-11 font-mono text-xs">
                {{ cc.phone_number || cc.recipient }}
              </td>
              <td class="px-3 py-2.5">
                <span
                  class="px-2 py-0.5 rounded text-[10px] font-medium uppercase"
                  :class="STATUS_COLORS[cc.status] || STATUS_COLORS.pending"
                >
                  {{ cc.status }}
                </span>
              </td>
              <td class="px-3 py-2.5 text-xs text-n-slate-10">
                {{ formatTime(cc.read_at) }}
              </td>
              <td class="px-3 py-2.5 text-xs text-n-slate-10">
                {{ formatTime(cc.replied_at) }}
              </td>
              <td
                class="px-3 py-2.5 text-xs max-w-[200px] truncate"
                :class="
                  cc.external_error ? 'text-n-ruby-11' : 'text-n-slate-10'
                "
                :title="cc.external_error"
              >
                {{ cc.external_error || '-' }}
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div
      v-if="meta.total_pages > 1"
      class="flex items-center justify-between text-xs text-n-slate-10"
    >
      <span>
        {{
          t('CAMPAIGN.WHATSAPP.ANALYTICS.SMART_TABLE.PAGE_INFO', {
            current: meta.current_page,
            total: meta.total_pages,
            count: meta.total_count,
          })
        }}
      </span>
      <div class="flex items-center gap-1">
        <button
          type="button"
          class="px-2 py-1 rounded hover:bg-n-alpha-2 disabled:opacity-30 cursor-pointer"
          :disabled="currentPage <= 1"
          @click="currentPage -= 1"
        >
          <span class="i-lucide-chevron-left size-4" />
        </button>
        <button
          type="button"
          class="px-2 py-1 rounded hover:bg-n-alpha-2 disabled:opacity-30 cursor-pointer"
          :disabled="currentPage >= meta.total_pages"
          @click="currentPage += 1"
        >
          <span class="i-lucide-chevron-right size-4" />
        </button>
      </div>
    </div>
  </div>
</template>
